import React, { useState, useEffect, useMemo } from 'react';
import { 
  Wifi, Monitor, Smartphone, Printer, ShieldAlert, 
  Loader2, Search, ChevronLeft, ChevronRight, Database,
  Globe, Activity, Filter, Download, Terminal
} from 'lucide-react';

const MapaRede = ({ showToast }) => {
  const [todosDispositivos, setTodosDispositivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [redeSelecionada, setRedeSelecionada] = useState('TODAS');
  const [termoFiltro, setTermoFiltro] = useState('');
  
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 8;

  useEffect(() => {
    carregarTodasAsRedes();
  }, []);

  const carregarTodasAsRedes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/monitoramento/todas?t=${Date.now()}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setTodosDispositivos(data);
      }
    } catch (err) {
      if (showToast) showToast("Erro ao carregar dados do servidor.", "error");
    } finally {
      setLoading(false);
    }
  };

  const listaRedes = useMemo(() => {
    const redes = todosDispositivos
      .map(d => d.nome_rede)
      .filter((value, index, self) => value && self.indexOf(value) === index);
    return ['TODAS', ...redes];
  }, [todosDispositivos]);

  const dispositivosFiltrados = useMemo(() => {
    return todosDispositivos.filter(d => {
      const matchesRede = redeSelecionada === 'TODAS' || d.nome_rede === redeSelecionada;
      const matchesBusca = d.hostname?.toLowerCase().includes(termoFiltro.toLowerCase()) || 
                          d.ip_address?.includes(termoFiltro) ||
                          d.mac_address?.toLowerCase().includes(termoFiltro.toLowerCase());
      return matchesRede && matchesBusca;
    });
  }, [todosDispositivos, redeSelecionada, termoFiltro]);

  const getIcon = (name) => {
    const n = name?.toLowerCase() || '';
    if (n.includes('print')) return <Printer size={28} />;
    if (n.includes('android') || n.includes('phone') || n.includes('iphone')) return <Smartphone size={28} />;
    if (n.includes('dvr') || n.includes('hik') || n.includes('cam')) return <ShieldAlert size={28} />;
    return <Monitor size={28} />;
  };

  const totalPaginas = Math.ceil(dispositivosFiltrados.length / itensPorPagina);
  const itensExibidos = dispositivosFiltrados.slice((paginaAtual - 1) * itensPorPagina, paginaAtual * itensPorPagina);

  return (
    <div className="space-y-6 pb-20 animate-in fade-in duration-500">
      
      {/* SEÇÃO DO AGENTE EXECUTÁVEL (NOVO) */}
      <div className="relative group overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 border border-blue-500/30 p-8 rounded-[40px] shadow-2xl">
        <div className="absolute top-0 right-0 p-10 opacity-10 group-hover:opacity-20 transition-opacity">
          <Terminal size={120} className="text-white" />
        </div>
        
        <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-4 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 rounded-full border border-blue-400/30">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              <span className="text-[10px] font-black text-blue-300 uppercase tracking-widest">Scanner Portátil v2.0</span>
            </div>
            <h2 className="text-4xl font-black text-white tracking-tighter">
              Auditoria <span className="text-blue-400">Instantânea</span>
            </h2>
            <p className="text-slate-300 text-sm max-w-md font-medium leading-relaxed">
              Baixe o agente, execute em qualquer computador da escola e veja os dispositivos aparecerem no mapa automaticamente. Não requer instalação.
            </p>
          </div>

          <a 
            href="/downloads/agente_ctc.exe" 
            download
            className="group/btn relative flex items-center gap-4 bg-white hover:bg-blue-50 text-slate-900 px-10 py-6 rounded-[30px] font-black text-xs uppercase tracking-widest shadow-2xl transition-all hover:-translate-y-1 active:scale-95"
          >
            <div className="p-3 bg-blue-600 rounded-2xl text-white group-hover/btn:rotate-12 transition-transform">
              <Download size={24} />
            </div>
            <span>Baixar Agente (.exe)</span>
          </a>
        </div>
      </div>

      {/* FILTROS E CONTROLE */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-[40px] border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col md:flex-row gap-4 items-center">
        <div className="relative w-full md:w-72">
          <Filter className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <select 
            className="w-full pl-14 pr-4 py-5 bg-slate-50 dark:bg-slate-950 border-2 border-slate-100 dark:border-slate-800 rounded-[24px] outline-none focus:border-blue-500 dark:text-white font-black text-xs appearance-none cursor-pointer"
            value={redeSelecionada}
            onChange={(e) => { setRedeSelecionada(e.target.value); setPaginaAtual(1); }}
          >
            {listaRedes.map(rede => (
              <option key={rede} value={rede}>{rede === 'TODAS' ? '🌐 TODAS AS REDES' : `📡 ${rede}`}</option>
            ))}
          </select>
        </div>

        <div className="relative flex-1 w-full">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="FILTRAR POR IP, NOME OU MAC..." 
            className="w-full pl-14 pr-4 py-5 bg-slate-50 dark:bg-slate-950 border-2 border-slate-100 dark:border-slate-800 rounded-[24px] outline-none focus:border-blue-500 dark:text-white font-black text-xs transition-all uppercase"
            value={termoFiltro}
            onChange={(e) => { setTermoFiltro(e.target.value); setPaginaAtual(1); }}
          />
        </div>

        <button 
          onClick={carregarTodasAsRedes}
          className="p-5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-[24px] hover:bg-blue-600 hover:text-white transition-all shadow-lg shadow-slate-200 dark:shadow-none"
          title="Recarregar dados"
        >
          <Activity size={20} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* GRID DE DISPOSITIVOS */}
      {loading ? (
        <div className="flex flex-col items-center justify-center p-32">
          <Loader2 className="animate-spin text-blue-500 mb-6" size={60} />
          <p className="text-slate-500 font-black text-xs uppercase tracking-widest animate-pulse">Sincronizando base de dados...</p>
        </div>
      ) : itensExibidos.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {itensExibidos.map((dev, idx) => (
              <div key={idx} className="group relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-[35px] blur opacity-0 group-hover:opacity-40 transition duration-500"></div>
                <div className="relative bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-slate-200 dark:border-slate-800 shadow-sm transition-all duration-300 group-hover:-translate-y-2">
                  
                  <div className="absolute top-5 right-6 px-3 py-1 bg-emerald-500/10 text-emerald-500 rounded-lg text-[7px] font-black uppercase border border-emerald-500/20">
                    Online
                  </div>

                  <div className="w-16 h-16 mx-auto mb-6 rounded-3xl flex items-center justify-center bg-slate-50 dark:bg-slate-800 text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                    {getIcon(dev.hostname)}
                  </div>
                  
                  <h3 className="text-center font-black dark:text-white text-[11px] truncate uppercase mb-1 tracking-tight">
                    {dev.hostname || 'EQUIPAMENTO'}
                  </h3>
                  <p className="text-center text-[8px] font-bold text-slate-400 uppercase mb-6 tracking-widest">
                    {dev.nome_rede || 'REDE LOCAL'}
                  </p>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                      <span className="text-[8px] font-black text-slate-400 uppercase">IP</span>
                      <span className="font-mono font-black text-blue-600 dark:text-blue-400 text-[10px]">{dev.ip_address}</span>
                    </div>
                    <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                      <span className="font-bold text-slate-400 text-[8px] uppercase">MAC</span>
                      <span className="font-mono font-bold dark:text-slate-300 text-[9px]">{dev.mac_address}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* PAGINAÇÃO */}
          <div className="flex justify-center items-center gap-8 mt-12 bg-white dark:bg-slate-900 w-fit mx-auto p-4 rounded-[24px] border border-slate-200 dark:border-slate-800 shadow-xl">
            <button disabled={paginaAtual === 1} onClick={() => setPaginaAtual(p => p - 1)} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-400 disabled:opacity-20 hover:text-blue-500 transition-all">
              <ChevronLeft size={24} />
            </button>
            <div className="flex flex-col items-center min-w-[140px]">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Total: {dispositivosFiltrados.length}</span>
              <span className="text-xl font-black dark:text-white">{paginaAtual} de {totalPaginas}</span>
            </div>
            <button disabled={paginaAtual === totalPaginas} onClick={() => setPaginaAtual(p => p + 1)} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-400 disabled:opacity-20 hover:text-blue-500 transition-all">
              <ChevronRight size={24} />
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center p-32 bg-slate-50/50 dark:bg-slate-900/30 rounded-[50px] border-4 border-dashed border-slate-200 dark:border-slate-800 text-center">
          <div className="p-8 bg-white dark:bg-slate-900 rounded-full shadow-2xl mb-6">
            <Database size={60} className="text-slate-300" />
          </div>
          <h3 className="text-slate-500 font-black text-lg uppercase tracking-tighter">Base de Dados Vazia</h3>
          <p className="text-slate-400 text-xs font-bold mt-2 uppercase max-w-sm">
            Nenhuma auditoria foi detectada. Utilize o botão no topo para baixar o agente e iniciar o mapeamento.
          </p>
        </div>
      )}
    </div>
  );
};

export default MapaRede;